import { useState, useCallback } from "react";
import { useApi } from "../../../../../../api/ApiProvider";
import {
  BillPdfBatchItemDtoPdfTypeEnum,
  UpdateInsuranceTransactionDto,
} from "../../../../../../api-client";
import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { useBatchPrintPdf, BillPdfBatchResult } from "../../../../../../hooks/useBatchPrintPdf";
import {
  extractDocIdsFromBatchResults,
  mergeAndAutoDownload,
} from "../../../../../../utils/billing/documentDownload";
import { ExportBillingData } from "../../../../../../api-client";

export interface CopaymentPrintResult {
  billId: string;
  paperDocumentId?: string;
  billDocumentId?: string;
}

export interface UseCopaymentEditActionsReturn {
  isPrinting: boolean;
  isSaving: boolean;
  /** Print progress (0-100) when using batch print */
  printProgress: number;
  /**
   * Save all bills, then print using batch workflow.
   * Returns batch result with document IDs for local patching.
   */
  saveAndPrintBatch: (
    bills: ExportBillingData[],
    notes: Record<string, string>,
  ) => Promise<{
    batchResult: BillPdfBatchResult | null;
    docIdMap: Map<string, CopaymentPrintResult>;
  }>;
  /** Save all bills sequentially (persist notes + rxIds + amount). */
  saveAll: (bills: ExportBillingData[], notes: Record<string, string>) => Promise<void>;
}

/**
 * Build UpdateInsuranceTransactionDto from an ExportBillingData.
 */
function buildUpdateDto(
  bill: ExportBillingData,
  notes: Record<string, string>,
): UpdateInsuranceTransactionDto {
  const { payerIK, accountInfo, data, userInfo, wasExported } = bill;
  const { rxs, payerDto, totalPrice } = data;

  const rxsIds = rxs.map((rx) => rx.id).filter((id): id is string => !!id);
  const paperCenter = payerDto?.paperReceivingCenter?.ik;
  const digitalCenter = payerDto?.digitalReceivingCenter?.ik;

  return {
    payerId: payerIK,
    accountId: accountInfo.id,
    amount: totalPrice,
    userId: userInfo.id!,
    rxIds: rxsIds,
    paperCenterId: paperCenter || undefined,
    dataCenterId: digitalCenter || undefined,
    notes: notes[bill.billId] || "",
    wasExported: wasExported,
    tAccountId: rxs[0]?.tarifGroupId === "Privat" ? 4105 : 4101,
  };
}

/**
 * Orchestrates copayment edit actions: Save and Print.
 *
 * - Save: sequential per bill (sends notes + rxIds via submitEditedBillingData).
 * - Print: batch workflow using FlowProducer for parallel PDF generation.
 *
 * The print workflow uses `INSURANCE_COPAYMENT_BILL` type which generates
 * two documents: paperCenterReceipt + billPdf (same as GKV).
 */
export function useCopaymentEditActions(): UseCopaymentEditActionsReturn {
  const { transaction } = useApi();
  const transactionStore = useStore(TransactionsContext);
  const { submitEditedBillingData } = transactionStore;

  const [isSaving, setIsSaving] = useState(false);

  // Batch print hook - uses FlowProducer for parallel PDF generation
  const {
    isLoading: isBatchPrinting,
    progress: printProgress,
    startBatchPrint,
  } = useBatchPrintPdf(BillPdfBatchItemDtoPdfTypeEnum.InsuranceCopaymentBill);

  /**
   * Save all bills sequentially (notes + rxIds + amount).
   */
  const saveAll = useCallback(
    async (bills: ExportBillingData[], notes: Record<string, string>): Promise<void> => {
      setIsSaving(true);
      try {
        for (const bill of bills) {
          const dto = buildUpdateDto(bill, notes);
          await submitEditedBillingData(bill.billId, dto);
        }
        notificationStore.showMessage("Alle Transaktionen erfolgreich aktualisiert", "success");
      } catch (error) {
        notificationStore.showMessage("Fehler beim Aktualisieren der Transaktionen", "error");
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [submitEditedBillingData],
  );

  /**
   * Save all bills first, then print using batch workflow.
   * Returns batch result and a map of billId -> document IDs for local patching.
   */
  const saveAndPrintBatch = useCallback(
    async (
      bills: ExportBillingData[],
      notes: Record<string, string>,
    ): Promise<{
      batchResult: BillPdfBatchResult | null;
      docIdMap: Map<string, CopaymentPrintResult>;
    }> => {
      const docIdMap = new Map<string, CopaymentPrintResult>();

      // Step 1: Save all bills first
      setIsSaving(true);
      try {
        for (const bill of bills) {
          const dto = buildUpdateDto(bill, notes);
          await submitEditedBillingData(bill.billId, dto);
        }
      } catch (error) {
        setIsSaving(false);
        notificationStore.showMessage("Fehler beim Speichern vor dem Drucken", "error");
        return { batchResult: null, docIdMap };
      }
      setIsSaving(false);

      // Step 2: Print all bills using batch workflow
      const billIds = bills.map((b) => b.billId);
      const result = await startBatchPrint(billIds);

      if (result) {
        // Build document ID map for local state patching
        for (const jobResult of result.results) {
          if (jobResult.success) {
            docIdMap.set(jobResult.entityId, {
              billId: jobResult.entityId,
              paperDocumentId: (jobResult as any).paperDocumentId,
              billDocumentId: (jobResult as any).billDocumentId,
            });
          }
        }

        // Merge and download documents
        const successfulResults = result.results.filter((r) => r.success);
        const docIds = extractDocIdsFromBatchResults(successfulResults);
        await mergeAndAutoDownload(docIds, (req) => transaction.mergeDirectBillingDocuments(req));

        if (result.failureCount > 0) {
          notificationStore.showMessage(
            `${result.successCount}/${result.totalCount} PDFs erfolgreich erstellt. ${result.failureCount} Fehler.`,
            "warning",
          );
        } else {
          notificationStore.showMessage(
            `Alle ${result.totalCount} PDFs erfolgreich erstellt.`,
            "success",
          );
        }

        // Signal MobX table to refetch
        transactionStore.lastUpdate = new Date();
      }

      return { batchResult: result, docIdMap };
    },
    [submitEditedBillingData, startBatchPrint, transaction, transactionStore],
  );

  return {
    isPrinting: isBatchPrinting,
    isSaving,
    printProgress,
    saveAndPrintBatch,
    saveAll,
  };
}
